import React, { useEffect } from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import { DatePicker, Input, Select } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { useSimpleLoyaltyList } from 'api/simple';
import { useTransactionTypeList } from 'api/transactionTypes';
import { useAppTriggerList } from 'api/triggers';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { wizardLinks } from '../wizardLinks';
import { PickedItem } from '../wizardModel';
import { FIELD_WIDTH, FieldWidth, wz } from '../wizardStyles';
import { CreateNewLink } from './pickers';

const Sized = styled.span<{ $width: number }>`
  display: inline-flex;
  flex: none;
  width: ${({ $width }) => $width}px;

  > * {
    width: 100%;
  }
`;

interface ITextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  width?: FieldWidth;
  placeholder?: string;
  disabled?: boolean;
  inputMode?: 'decimal' | 'numeric' | 'text';
}

/** 6778:18441 `Input inp`: 36.4 tall, r8, the placeholder in `#757575`. */
export const TextField: React.FC<ITextFieldProps> = ({
  label,
  value,
  onChange,
  width = 'sm',
  placeholder,
  disabled = false,
  inputMode = 'text'
}) => (
  <Sized $width={FIELD_WIDTH[width]}>
    <Input
      aria-label={label}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      inputMode={inputMode}
      onChange={event => onChange(event.target.value)}
    />
  </Sized>
);

interface ISelectFieldProps<T extends string | number> {
  label: string;
  value: T | null;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  width?: FieldWidth;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  showSearch?: boolean;
}

/** 6778:19821 `Select inp`: the field box with a 14 chevron, a dark menu (6778:20059). */
export const SelectField = <T extends string | number>({
  label,
  value,
  options,
  onChange,
  width = 'sm',
  placeholder,
  disabled = false,
  loading = false,
  showSearch = false
}: ISelectFieldProps<T>) => (
  <Sized $width={FIELD_WIDTH[width]}>
    <Select<T>
      aria-label={label}
      value={value ?? undefined}
      placeholder={placeholder}
      disabled={disabled}
      loading={loading}
      showSearch={showSearch}
      optionFilterProp="label"
      options={options}
      onChange={next => onChange(next)}
    />
  </Sized>
);

interface IDateFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  withTime?: boolean;
  disabled?: boolean;
}

// 6778:18563 `withic` draws the calendar in front of the value; the picker keeps it behind.
export const DateField: React.FC<IDateFieldProps> = ({
  label,
  value,
  onChange,
  withTime = false,
  disabled = false
}) => (
  <Sized $width={withTime ? 196 : 172}>
    <DatePicker
      aria-label={label}
      value={value === null ? null : moment(value)}
      format={withTime ? 'YYYY-MM-DD HH:mm' : 'DD / MM / YYYY'}
      placeholder={withTime ? 'yyyy-mm-dd hh:mm' : 'dd / mm / yyyy'}
      showTime={withTime ? { format: 'HH:mm' } : false}
      disabled={disabled}
      suffixIcon={<CalendarOutlined style={{ color: wz.secondary }} />}
      onChange={next => onChange(next === null ? null : next.format())}
    />
  </Sized>
);

interface ILoyaltySelectProps {
  value: PickedItem | null;
  onChange: (value: PickedItem) => void;
  width?: FieldWidth;
}

/**
 * The tenant's loyalty programmes, "Name (id: n)". The first is picked for the user until they
 * choose, as the Stamp FR defaults it.
 */
export const LoyaltySelect: React.FC<ILoyaltySelectProps> = ({ value, onChange, width = 'lg' }) => {
  const { t } = useMultiLanguageTranslation();
  const { data, isLoading } = useSimpleLoyaltyList();
  const first = data?.[0];

  useEffect(() => {
    if (value === null && first !== undefined) {
      onChange({ id: first.id, name: first.name });
    }
  }, [value, first, onChange]);

  return (
    <>
      <SelectField<number>
        label={t(`${TOrbit}.wizard.prize.pointsFrom`)}
        value={value?.id ?? null}
        width={width}
        loading={isLoading}
        options={(data ?? []).map(programme => ({
          value: programme.id,
          label: t(`${TOrbit}.wizard.prize.loyaltyOption`, {
            name: programme.name,
            id: programme.id
          })
        }))}
        onChange={id => {
          const programme = data?.find(item => item.id === id);
          if (programme !== undefined) {
            onChange({ id: programme.id, name: programme.name });
          }
        }}
      />
      <CreateNewLink
        to={wizardLinks.createLoyalty()}
        label={t(`${TOrbit}.wizard.createNew.loyalty`)}
      />
    </>
  );
};

export const TransactionTypeField: React.FC<{
  value: string | null;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
  const { t } = useMultiLanguageTranslation();
  const { data, isLoading } = useTransactionTypeList();
  return (
    <SelectField<string>
      label={t(`${TOrbit}.wizard.trigger.transaction.typePlaceholder`)}
      placeholder={t(`${TOrbit}.wizard.trigger.transaction.typePlaceholder`)}
      value={value}
      width="md"
      showSearch
      loading={isLoading}
      disabled={disabled}
      options={(data ?? [])
        .filter((type): type is string => type !== null)
        .map(type => ({ value: type, label: type }))}
      onChange={onChange}
    />
  );
};

export const AppTriggerSelect: React.FC<{
  value: PickedItem | null;
  onChange: (value: PickedItem) => void;
}> = ({ value, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  const { data, isLoading } = useAppTriggerList();
  return (
    <SelectField<number>
      label={t(`${TOrbit}.wizard.trigger.others.question`)}
      placeholder={t(`${TOrbit}.wizard.trigger.others.placeholder`)}
      value={value?.id ?? null}
      width="xl"
      showSearch
      loading={isLoading}
      options={(data ?? []).map(trigger => ({ value: trigger.id, label: trigger.name }))}
      onChange={id => {
        const trigger = data?.find(item => item.id === id);
        if (trigger !== undefined) {
          onChange({ id: trigger.id, name: trigger.name });
        }
      }}
    />
  );
};
